import { createContext, useContext, useState, ReactNode, useMemo } from 'react'
import { OrganSegmentationType } from 'types/exam/types'
import { ORGAN_OPACITY } from '../constants/organs'
import analytics from 'services/analytics'

interface OrganSelectionContextType {
  selected: OrganSegmentationType[]
  opacity: number
  toggleOrgan: (type: OrganSegmentationType) => void
  /** Marca os que faltam de `available`; se todos já estão marcados, desmarca todos. */
  toggleAll: (available: OrganSegmentationType[]) => void
  setOpacity: (opacity: number) => void
}

const OrganSelectionContext = createContext<
  OrganSelectionContextType | undefined
>(undefined)

interface OrganSelectionProviderProps {
  children: ReactNode
}

/** Seleção de órgãos e opacidade compartilhadas entre os modais da imagem original e das patologias. */
export const OrganSelectionProvider = ({
  children
}: OrganSelectionProviderProps) => {
  const [selected, setSelected] = useState<OrganSegmentationType[]>([])
  const [opacity, setOpacity] = useState(ORGAN_OPACITY.default)

  const value = useMemo(
    () => ({
      selected,
      opacity,
      setOpacity,
      toggleOrgan: (type: OrganSegmentationType) => {
        analytics.track('xray_organs_toggled', {
          organ: type,
          visible: !selected.includes(type)
        })
        setSelected(previous =>
          previous.includes(type)
            ? previous.filter(item => item !== type)
            : [...previous, type]
        )
      },
      toggleAll: (available: OrganSegmentationType[]) => {
        analytics.track('xray_organs_toggled', {
          organ: 'all',
          visible: !available.every(type => selected.includes(type))
        })
        setSelected(previous =>
          available.every(type => previous.includes(type)) ? [] : available
        )
      }
    }),
    [selected, opacity]
  )

  return (
    <OrganSelectionContext.Provider value={value}>
      {children}
    </OrganSelectionContext.Provider>
  )
}

export const useOrganSelection = () => {
  const context = useContext(OrganSelectionContext)
  if (!context) {
    throw new Error(
      'useOrganSelection must be used within OrganSelectionProvider'
    )
  }
  return context
}
